// CIM Command Center (#/cim/<engagementId>) — the dealmaker's cockpit for
// one seller deal's CIM (routes/cim.py, app/cim_center.py).
//
// Tabs (the hash carries ?tab=, replaceState):
//   Overview              the five-step path (Gather data → Check the numbers →
//                         Write → Review → Deliver, computed by cim_center.path:
//                         each step's state, one count line, one action), next
//                         actions, Essential items still open, recent activity,
//                         owners, the thesis at a glance
//   Data room             views/data_room.jsx (window.DataRoomPane)
//   Vault                 per-folder tiles; a tile opens the vault manager
//                         (views/company_vault.jsx, window.VaultManager)
//   Company intelligence  the cim-architecture runs: launch / live progress /
//                         cancel, then the thesis, the 11 deliverables, the 17
//                         section plans (each reviewable), the questionnaire
//                         (chosen questions → draft owner questions), versions
//                         and the diff between two of them
//   Ledger                the facts ledger: views/cim_ledger.jsx
//                         (window.CimLedgerTab); a dot while an extraction runs
//   Sections              the 17 sections: views/cim_sections.jsx
//                         (window.CimSectionsTab) — builds, the critic, versions,
//                         approval; ?tab=sections&section=11&v=2 opens one
//
// A running architecture is polled every 5 s for its progress lines
// (window.usePolling). Model-written text renders only through
// <window.Markdown strict> or as plain text. What a run cites by id — notes,
// vault files, data-room requests, questionnaire codes — arrives resolved
// (app/cim_refs.py) and renders as names; a note or a file opens in place.
// Timestamps, never durations or spend.
//
// window.CimCommandCenter({ engagementId, phone })

const CIM_TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'room', label: 'Data room' },
  { key: 'vault', label: 'Vault' },
  { key: 'intelligence', label: 'Company intelligence' },
  { key: 'ledger', label: 'Ledger' },
  { key: 'sections', label: 'Sections' },
  { key: 'book', label: 'Book' },
];
const CIM_TAB_KEYS = CIM_TABS.map((t) => t.key);
const CIM_ACTIVE = new Set(['queued', 'running', 'validating']);
const CIM_TERMINAL = new Set(['completed', 'failed', 'cancelled']);

// key → [label, hue]
const CIM_RUN_STATUS = {
  queued: ['Queued', 'slate'], running: ['Running', 'blue'], validating: ['Validating', 'amber'],
  completed: ['Completed', 'green'], failed: ['Failed', 'rose'], cancelled: ['Cancelled', 'slate'],
};
const CIM_REVIEW = { draft: ['Draft', 'slate'], reviewed: ['Reviewed', 'blue'], approved: ['Approved', 'green'], rejected: ['Rejected', 'rose'] };
const CIM_READY = { not_started: ['Not started', 'slate'], gathering_evidence: ['Gathering evidence', 'amber'], ready_to_draft: ['Ready to draft', 'green'] };
const CIM_CONFIDENCE = { high: ['High confidence', 'green'], medium: ['Medium confidence', 'amber'], low: ['Low confidence', 'rose'] };
const CIM_ITEM_HUE = { none: 'slate', prog: 'blue', up: 'teal', rev: 'amber', fix: 'rose', ok: 'green', na: 'slate' };
const CIM_PRIORITY = { P0: 'Essential', P1: 'High value', P2: 'Supplemental' };
const CIM_RESPONSE = { document: 'Document', interview: 'Interview', written: 'Written answer', calculation: 'Calculation' };
const CIM_EVENT = {
  status: ['Status', 'var(--text-2)'], tool: ['Read', 'var(--text-3)'], save: ['Saved', 'var(--green-fg)'],
  web: ['Web', 'var(--accent-text)'], note: ['Note', 'var(--text-3)'], check: ['Check', 'var(--amber-fg)'],
};
const CIM_DELIVERABLES = {
  1: 'Company Intelligence Brief', 2: 'PE Buyer Interest Assessment', 3: 'Top Ten Potential Investment Drivers',
  4: 'Top Five Deal Killers', 5: 'Customized CIM Table of Contents', 6: 'Section-by-Section CIM Questionnaire',
  7: 'Five Highest-Impact CIM Exhibits', 8: 'Prioritized Management Discovery Plan', 9: 'Consolidated Initial Data Request',
  10: 'CIM Readiness Scorecard', 11: 'Machine-Readable Section Plans',
};
// The folders whose Ready for CIM feeds the sections (not CIM working files, not To sort).
const CIM_DATA_FOLDERS = new Set(['company_owners', 'products_customers', 'operations_employees', 'financials',
  'market_competitors', 'growth_plans', 'calls_notes']);
const CIM_DRIVER_FIELDS = [
  ['buyer_rationale', 'Why buyers care'], ['mechanism', 'Mechanism'], ['evidence', 'Evidence'],
  ['proof_required', 'Proof required'], ['exhibit', 'Exhibit'], ['cim_treatment', 'CIM treatment'],
  ['owner_questions', 'Owner questions'],
];
const CIM_KILLER_FIELDS = [
  ['why_buyers_care', 'Why buyers care'], ['cim_treatment', 'CIM treatment'], ['quantifying_questions', 'Quantifying questions'],
];
const CIM_HANDOFF_LABELS = {
  section_id: 'Section', internal_title: 'Internal title', section_skill: 'Section skill', external_chapter: 'External chapter',
  buyer_thesis: 'Buyer thesis', question_plan: 'Question plan', evidence_ids: 'Evidence', canonical_metric_requests: 'Canonical metric requests',
  draft_readiness: 'Readiness', exhibit_recommendations: 'Exhibits', sources: 'Sources', risks: 'Risks', atlas_folders: 'Vault folders',
  storage_status: 'Storage',
};
// Handoff fields whose ids the server resolves (handoff.display).
const CIM_REF_FIELDS = new Set(['sources', 'evidence_ids', 'exhibit_recommendations']);
const CIM_NEXT_ICON = {
  running: 'Refresh', run: 'Play', approve: 'Check', submitted: 'Upload', access: 'Users', unread: 'Clock', curate: 'Folder',
  reading: 'Refresh', read: 'Play', conflicts: 'Flag', approve_numbers: 'Check', lock: 'Check', building: 'Refresh', build: 'Play',
  review_sections: 'Doc', rendering: 'Refresh', render: 'Play', final: 'Check',
};
// The path's step states (cim_center.path): the word for each, shown as the mark's tooltip
// and read out with it — the mark's shape carries it too, never color alone.
const CIM_PATH_STATE = { done: 'Done', now: 'Next', running: 'Running', waiting: 'Waiting' };

const _cimCard = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10 };
const _cimCap = { fontSize: 10.5, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-4)' };
const _cimInput = {
  width: '100%', fontSize: 12.5, padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)',
  background: 'var(--surface)', color: 'var(--text)', boxSizing: 'border-box',
};
const _cimRowBtn = { display: 'flex', alignItems: 'center', gap: 10, width: '100%', border: 'none', background: 'transparent', textAlign: 'left', cursor: 'pointer', color: 'var(--text)' };
const _cimLink = { border: 'none', background: 'transparent', padding: 0, font: 'inherit', color: 'var(--accent-text)', cursor: 'pointer', textAlign: 'left', textDecoration: 'none', overflowWrap: 'anywhere' };
const _cimCode = { fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--text-3)', flexShrink: 0, minWidth: 40 };

const _cimTabFromHash = (hash) => {
  try {
    const t = new URLSearchParams(((hash != null ? hash : window.location.hash).split('?')[1] || '')).get('tab');
    return CIM_TAB_KEYS.includes(t) ? t : 'overview';
  } catch (_) { return 'overview'; }
};

// The hash a tab change writes (replaceState), or null: another page's route,
// or the same tab — which keeps its own params (the Sections tab's section and
// version, ?tab=sections&section=11&v=2).
const _cimTabHash = (cur, base, tab) => {
  if (!(cur === base || cur.startsWith(`${base}?`))) return null;
  if (_cimTabFromHash(cur) === tab) return null;
  const next = tab === 'overview' ? base : `${base}?tab=${tab}`;
  return cur === next ? null : next;
};

// apiFetch throws "404 Not Found: {"detail": "…"}" — the status and the
// server's sentence.
const _cimErr = (e) => {
  const msg = (e && e.message) || '';
  const m = /^(\d{3})\b[^:]*:\s*([\s\S]*)$/.exec(msg);
  if (!m) return { status: (e && e.status) || null, detail: msg || 'Something went wrong' };
  let detail = m[2].trim();
  try {
    const j = JSON.parse(detail);
    if (j && j.detail) detail = typeof j.detail === 'string' ? j.detail : JSON.stringify(j.detail);
  } catch (_) { /* not JSON */ }
  return { status: Number(m[1]), detail: detail || `Error ${m[1]}` };
};
const _cimToastErr = (title, e) => window.pushToast && window.pushToast({ type: 'error', title, body: _cimErr(e).detail });

const _cimWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const t = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return d.toDateString() === new Date().toDateString() ? t : `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}, ${t}`;
};
const _cimClock = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', second: '2-digit' });
};
const _cimPlural = (n, one, many) => `${n} ${n === 1 ? one : (many || `${one}s`)}`;
const _cimPad = (n) => String(n).padStart(2, '0');
const _cimHuman = (k) => String(k || '').replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
const _cimByRank = (a, b) => (a.rank || 99) - (b.rank || 99);
const _cimEmpty = (v) => v == null || v === '' || (Array.isArray(v) && v.length === 0);
// "6.01" / "06.01" → [6, 1] for sorting and matching.
const _cimQid = (id) => {
  const m = /^0?(\d{1,2})\.(\d{1,3})$/.exec(String(id || '').trim());
  return m ? [Number(m[1]), Number(m[2])] : [99, 999];
};
const _cimQkey = (id) => { const [s, n] = _cimQid(id); return `${s}.${n}`; };
const _cimMergeEvents = (cur, next) => {
  if (!next || !next.length) return cur;
  const seen = new Set(cur.map((e) => e.id));
  const out = cur.concat(next.filter((e) => !seen.has(e.id)));
  return out.length > 1500 ? out.slice(-1500) : out;
};

// external_chapter is {title, number} or "suppressed — immaterial: why".
const _cimChapter = (ch) => {
  if (ch == null || ch === '') return { label: '—', suppressed: false, reason: '' };
  const SUP = /^\s*suppressed\b\s*[—–:-]*\s*(immaterial)?\s*[—–:-]*\s*/i;
  if (typeof ch === 'string') {
    const s = SUP.test(ch);
    return { label: s ? 'Suppressed' : ch, suppressed: s, reason: s ? ch.replace(SUP, '') : '' };
  }
  if (typeof ch === 'object') {
    const title = String(ch.title || '');
    const s = !!ch.suppressed || SUP.test(title) || /^\s*suppressed/i.test(String(ch.status || ''));
    if (s) return { label: 'Suppressed', suppressed: true, reason: String(ch.reason || title.replace(SUP, '') || '') };
    const num = ch.number != null && ch.number !== '' ? `Ch ${ch.number}` : null;
    return { label: [num, title].filter(Boolean).join(' · ') || '—', suppressed: false, reason: '' };
  }
  return { label: String(ch), suppressed: false, reason: '' };
};

const _cimHover = (on) => (on ? {
  onMouseEnter: (e) => { e.currentTarget.style.background = 'var(--surface-hover)'; },
  onMouseLeave: (e) => { e.currentTarget.style.background = 'transparent'; },
} : {});

// ── small pieces ───────────────────────────────────────────────────────────

const CimPill = ({ map, k, dot = false, sz = 'sm' }) => {
  const [label, hue] = map[k] || [k ? _cimHuman(k) : '—', 'slate'];
  return <window.Pill hue={hue} sz={sz} dot={dot}>{label}</window.Pill>;
};

// Model text: strict markdown only.
const CimMd = ({ children, phone, compact = false }) => {
  const src = (children == null ? '' : children).toString();
  if (!src.trim()) return null;
  return (
    <div style={{ fontSize: compact ? 12.5 : phone ? 15 : 13.5, lineHeight: 1.6, color: 'var(--text-2)', overflowWrap: 'anywhere', overflowX: 'auto' }}>
      {window.Markdown
        ? <window.Markdown strict className="md-reader">{src}</window.Markdown>
        : <div style={{ whiteSpace: 'pre-wrap' }}>{src}</div>}
    </div>
  );
};

// Any handoff value as plain text: strings, lists, small objects.
const CimValue = ({ v }) => {
  if (_cimEmpty(v)) return <span style={{ color: 'var(--text-4)' }}>—</span>;
  if (Array.isArray(v)) {
    return (
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        {v.map((x, i) => <li key={i} style={{ margin: '2px 0' }}><CimValue v={x} /></li>)}
      </ul>
    );
  }
  if (typeof v === 'object') {
    const entries = Object.entries(v).filter(([, x]) => !_cimEmpty(x));
    if (!entries.length) return <span style={{ color: 'var(--text-4)' }}>—</span>;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {entries.map(([k, x]) => (
          <div key={k}><span style={{ color: 'var(--text-4)' }}>{_cimHuman(k)}: </span><CimValue v={x} /></div>
        ))}
      </div>
    );
  }
  if (typeof v === 'boolean') return <span>{v ? 'Yes' : 'No'}</span>;
  return <span style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{String(v)}</span>;
};

// One line the server resolved: text, plus notes and vault files by name
// (onRef opens them in place; a note is also a real #/notes link), request
// titles, and question codes with the question as their hint.
const CimRefText = ({ segs, onRef }) => {
  if (!segs || !segs.length) return <span style={{ color: 'var(--text-4)' }}>—</span>;
  return (
    <span style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
      {segs.map((s, i) => {
        if (s.kind === 'note') {
          return (
            <a key={i} href={`#/notes/${s.id}`} style={_cimLink}
              onClick={(e) => { if (!onRef || e.metaKey || e.ctrlKey || e.shiftKey) return; e.preventDefault(); onRef(s); }}>{s.text}</a>
          );
        }
        if (s.kind === 'file' && onRef) return <button key={i} type="button" style={_cimLink} onClick={() => onRef(s)}>{s.text}</button>;
        if (s.kind === 'question' || s.kind === 'missing') {
          return (
            <span key={i} title={s.hint} style={{ textDecoration: 'underline dotted', textUnderlineOffset: 2, cursor: 'help',
              color: s.kind === 'missing' ? 'var(--text-4)' : undefined }}>{s.text}</span>
          );
        }
        return <span key={i} title={s.hint || undefined} style={s.kind === 'request' ? { color: 'var(--text)' } : undefined}>{s.text}</span>;
      })}
    </span>
  );
};

// Sources, evidence and exhibits of a handoff, from handoff.display.
const CimRefField = ({ f, disp, onRef }) => {
  const rows = (f === 'sources' ? disp.sources : f === 'evidence_ids' ? disp.evidence : disp.exhibits) || [];
  if (!rows.length) return <CimValue v={null} />;
  if (f === 'sources') {
    return (
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        {rows.map((segs, i) => <li key={i} style={{ margin: '2px 0' }}><CimRefText segs={segs} onRef={onRef} /></li>)}
      </ul>
    );
  }
  if (f === 'exhibit_recommendations') {
    return (
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        {rows.map((x, i) => (
          <li key={i} style={{ margin: '3px 0' }}>
            {x.title && <span style={{ color: 'var(--text)', fontWeight: 500 }}>{x.title}</span>}
            {(x.segs || []).length > 0 && <span style={{ color: 'var(--text-3)' }}>{x.title ? ' — ' : ''}<CimRefText segs={x.segs} onRef={onRef} /></span>}
          </li>
        ))}
      </ul>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {rows.map((e, i) => (
        <div key={i} style={{ minWidth: 0 }}>
          {(e.code || e.title) && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', marginBottom: 2 }}>
              {e.code && <span style={{ ..._cimCode, minWidth: 0 }}>{e.code}</span>}
              {e.title && <span style={{ color: 'var(--text)', fontWeight: 500, minWidth: 0, overflowWrap: 'anywhere' }}>{e.title}</span>}
            </div>
          )}
          {(e.segs || []).length > 0 && <CimRefText segs={e.segs} onRef={onRef} />}
          {(e.fields || []).length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: '76px minmax(0, 1fr)', columnGap: 8, rowGap: 1 }}>
              {e.fields.map((x, j) => (
                <React.Fragment key={`${x.label}-${j}`}>
                  <span style={{ color: 'var(--text-4)' }}>{x.label}</span>
                  <CimRefText segs={x.segs} onRef={onRef} />
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// A driver's owner questions / a deal killer's quantifying questions:
// [{code, text}] with the text from the run's questionnaire.
const CimQuestionRefs = ({ rows }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
    {rows.map((q, i) => (
      <div key={`${q.code}-${i}`} style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
        {q.code && <span style={_cimCode}>{q.code}</span>}
        <span style={{ minWidth: 0, overflowWrap: 'anywhere', color: q.text ? 'var(--text-2)' : 'var(--text-4)' }}>{q.text || 'Not in this version'}</span>
      </div>
    ))}
  </div>
);

const CimPanel = ({ title, count, action, children, style }) => (
  <section style={{ ..._cimCard, minWidth: 0, ...style }}>
    <header style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderBottom: '1px solid var(--border)', minHeight: 42, boxSizing: 'border-box' }}>
      <h3 style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', margin: 0 }}>{title}</h3>
      {count != null && <span style={{ fontSize: 12, color: 'var(--text-4)', fontVariantNumeric: 'tabular-nums' }}>{count}</span>}
      <div style={{ flex: 1 }} />
      {action}
    </header>
    {children}
  </section>
);

const CimEmptyLine = ({ children }) => (
  <div style={{ padding: '14px', fontSize: 12.5, color: 'var(--text-3)' }}>{children}</div>
);

// A step's mark: a check when done, the refresh glyph while running, its number
// otherwise — filled when it's the step to do now, outlined while it waits.
const CimStepMark = ({ state, n }) => {
  const look = state === 'done' ? { background: 'var(--green-bg)', color: 'var(--green-fg)' }
    : state === 'running' ? { background: 'var(--accent-soft)', color: 'var(--accent-text)' }
      : state === 'now' ? { background: 'var(--accent)', color: '#fff' }
        : { border: '1px solid var(--border-strong)', color: 'var(--text-4)' };
  const Ico = state === 'done' ? window.Icon.Check : state === 'running' ? window.Icon.Refresh : null;
  const word = CIM_PATH_STATE[state] || '';
  return (
    <span role="img" aria-label={`Step ${n}: ${word}`} title={word}
      style={{ width: 22, height: 22, borderRadius: 999, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        boxSizing: 'border-box', fontSize: 11.5, fontWeight: 650, fontVariantNumeric: 'tabular-nums', ...look }}>
      {Ico ? <Ico size={12} /> : n}
    </span>
  );
};

// The five steps from kickoff to a delivered book (ov.path). The first step to do
// now (or running) is the current one; each step carries one action to its tab.
const CimPath = ({ steps, phone, goTab }) => {
  const at = steps.findIndex((s) => s.state === 'now' || s.state === 'running');
  return (
    <ol aria-label="CIM steps"
      style={{ ..._cimCard, listStyle: 'none', margin: 0, padding: 0, display: 'grid', overflow: 'hidden',
        gridTemplateColumns: phone ? 'minmax(0, 1fr)' : `repeat(${steps.length}, minmax(0, 1fr))` }}>
      {steps.map((s, i) => {
        const cur = i === at;
        const a = s.action || {};
        return (
          <li key={s.key} aria-current={cur ? 'step' : undefined}
            style={{ display: 'flex', flexDirection: phone ? 'row' : 'column', alignItems: phone ? 'center' : 'stretch', gap: phone ? 10 : 8,
              minWidth: 0, padding: phone ? '10px 12px' : '12px 14px', background: cur ? 'var(--accent-soft)' : 'transparent',
              borderTop: phone && i ? '1px solid var(--border)' : 'none', borderLeft: !phone && i ? '1px solid var(--border)' : 'none' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: phone ? 1 : 'none' }}>
              <CimStepMark state={s.state} n={i + 1} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: s.state === 'waiting' ? 'var(--text-3)' : 'var(--text)',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.label}</div>
                <div title={s.count} style={{ fontSize: 12, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.count}</div>
              </div>
            </div>
            {a.tab && (
              <div style={{ marginTop: phone ? 0 : 'auto', flexShrink: 0 }}>
                <window.Btn size="sm" variant={cur ? 'primary' : 'ghost'} onClick={() => goTab(a.tab, { filter: a.filter, sub: a.sub })}>
                  {a.label}
                </window.Btn>
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
};

const CimChevron = ({ open }) => (
  <span aria-hidden style={{ display: 'inline-flex', color: 'var(--text-4)', flexShrink: 0, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s ease' }}>
    {window.Icon && window.Icon.ChevronR ? <window.Icon.ChevronR size={11} /> : '›'}
  </span>
);

// ── review controls (deliverables and handoffs) ───────────────────────────

const CimReviewMeta = ({ item }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <CimPill map={CIM_REVIEW} k={item.review_status} dot />
      {item.review_status !== 'draft' && (item.reviewed_by_name || item.reviewed_at) && (
        <span style={{ fontSize: 11.5, color: 'var(--text-4)' }}>{[item.reviewed_by_name, _cimWhen(item.reviewed_at)].filter(Boolean).join(' · ')}</span>
      )}
    </div>
    {item.review_note && (
      <div style={{ borderLeft: `3px solid ${item.review_status === 'rejected' ? 'var(--rose-fg)' : 'var(--border-strong)'}`,
        background: item.review_status === 'rejected' ? 'var(--rose-bg)' : 'var(--surface-2)', borderRadius: 6, padding: '8px 10px',
        fontSize: 12.5, color: 'var(--text)', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{item.review_note}</div>
    )}
  </div>
);

const CimReviewBar = ({ status, busy, onReview }) => {
  const { useState } = React;
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState('');
  if (rejecting) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
        <textarea autoFocus value={note} rows={3} maxLength={4000} onChange={(e) => setNote(e.target.value)} aria-label="What's wrong"
          placeholder="What's wrong" style={{ ..._cimInput, resize: 'vertical' }} />
        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
          <window.Btn size="sm" variant="ghost" onClick={() => { setRejecting(false); setNote(''); }}>Cancel</window.Btn>
          <window.Btn size="sm" variant="accent" disabled={busy || !note.trim()}
            onClick={async () => { if (await onReview('rejected', note.trim())) { setRejecting(false); setNote(''); } }}>Reject</window.Btn>
        </div>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end', width: '100%' }}>
      {status !== 'draft' && <window.Btn size="sm" variant="ghost" disabled={busy} onClick={() => onReview('draft')}>Back to draft</window.Btn>}
      {status !== 'rejected' && <window.Btn size="sm" variant="ghost" disabled={busy} onClick={() => setRejecting(true)}>Reject</window.Btn>}
      {status !== 'reviewed' && <window.Btn size="sm" variant="outline" disabled={busy} onClick={() => onReview('reviewed')}>Mark reviewed</window.Btn>}
      {status !== 'approved' && (
        <window.Btn size="sm" variant="accent" disabled={busy} icon={<window.Icon.Check size={12} />} onClick={() => onReview('approved')}>Approve</window.Btn>
      )}
    </div>
  );
};

// ── drawers ────────────────────────────────────────────────────────────────

const CimDeliverableDrawer = ({ detail, number, canReview, busy, onReview, onClose, phone }) => {
  const d = (detail.deliverables || []).find((x) => x.number === number);
  return (
    <window.Drawer open onClose={onClose} width={820}
      title={`${number}. ${d ? d.title : CIM_DELIVERABLES[number] || 'Deliverable'}`}
      subtitle={`Architecture v${detail.run.version}`}
      footer={canReview && d ? (
        <CimReviewBar key={`d${number}-${d.review_status}`} status={d.review_status} busy={busy}
          onReview={(status, note) => onReview('deliverable', number, status, note)} />
      ) : null}>
      {d ? (
        <>
          <CimReviewMeta item={d} />
          <CimMd phone={phone}>{d.body_md}</CimMd>
        </>
      ) : <div style={{ fontSize: 13, color: 'var(--text-3)' }}>Not in this version.</div>}
    </window.Drawer>
  );
};

const CimHandoffDrawer = ({ detail, sectionNo, folderLabel, canReview, busy, onReview, onClose, onRef }) => {
  const h = (detail.handoffs || []).find((x) => x.section_no === sectionNo);
  const r = (h && h.record) || {};
  const refs = (f) => (h && h.display ? <CimRefField f={f} disp={h.display} onRef={onRef} /> : <CimValue v={r[f]} />);
  const questions = (detail.lists && detail.lists.question) || [];
  const qText = new Map(questions.map((q) => [_cimQkey(q.id || q.key), q.question]));
  const ch = _cimChapter(r.external_chapter);
  const field = (label, body) => (
    <div style={{ marginBottom: 14 }}>
      <div style={{ ..._cimCap, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.5 }}>{body}</div>
    </div>
  );
  const folders = r.atlas_folders || {};
  const folderList = (keys) => (keys && keys.length ? keys.map((k) => folderLabel(k)).join(', ') : '—');
  const plan = [...(r.question_plan || [])].sort((a, b) => {
    const x = _cimQid(a && a.id); const y = _cimQid(b && b.id);
    return x[0] - y[0] || x[1] - y[1];
  });
  return (
    <window.Drawer open onClose={onClose} width={680}
      title={`${_cimPad(sectionNo)} · ${r.internal_title || 'Section plan'}`}
      subtitle={`Section plan · architecture v${detail.run.version}`}
      footer={canReview && h ? (
        <CimReviewBar key={`h${sectionNo}-${h.review_status}`} status={h.review_status} busy={busy}
          onReview={(status, note) => onReview('handoff', sectionNo, status, note)} />
      ) : null}>
      {!h ? <div style={{ fontSize: 13, color: 'var(--text-3)' }}>Not in this version.</div> : (
        <>
          <CimReviewMeta item={h} />
          {field('External chapter', (
            <span>
              {ch.suppressed ? <window.Pill hue="slate" sz="sm">Suppressed</window.Pill> : ch.label}
              {ch.reason && <span style={{ color: 'var(--text-3)' }}> {ch.reason}</span>}
            </span>
          ))}
          {field('Readiness', <CimPill map={CIM_READY} k={r.draft_readiness} />)}
          {field('Buyer thesis', <CimValue v={r.buyer_thesis} />)}
          {field(`Question plan · ${plan.length}`, plan.length ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {plan.map((q, i) => (
                <div key={`${q.id}-${i}`} style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                  <span style={_cimCode}>{q.id}</span>
                  <window.Pill hue={q.priority === 'P0' ? 'violet' : 'slate'} sz="sm">{q.priority}</window.Pill>
                  <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{qText.get(_cimQkey(q.id)) || ''}</span>
                </div>
              ))}
            </div>
          ) : <CimValue v={null} />)}
          {field('Canonical metric requests', <CimValue v={r.canonical_metric_requests} />)}
          {field('Exhibits', refs('exhibit_recommendations'))}
          {field('Evidence', refs('evidence_ids'))}
          {field('Sources', refs('sources'))}
          {field('Risks', <CimValue v={r.risks} />)}
          {field('Vault folders', (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div><span style={{ color: 'var(--text-4)' }}>Primary: </span>{folderList(folders.primary)}</div>
              <div><span style={{ color: 'var(--text-4)' }}>Supporting: </span>{folderList(folders.supporting)}</div>
            </div>
          ))}
          {field('Section skill', <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5 }}>{r.section_skill || '—'}</span>)}
        </>
      )}
    </window.Drawer>
  );
};

const CimDiffLines = ({ lines, truncated }) => (
  <div style={{ marginTop: 8, border: '1px solid var(--border)', borderRadius: 6, overflowX: 'auto', fontFamily: 'var(--font-mono)', fontSize: 11.5, lineHeight: 1.5 }}>
    {lines.map((l, i) => {
      const add = l.startsWith('+'); const del = l.startsWith('-'); const hunk = l.startsWith('@@');
      return (
        <div key={i} style={{ padding: '0 8px', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere',
          background: add ? 'var(--green-bg)' : del ? 'var(--rose-bg)' : hunk ? 'var(--surface-2)' : 'transparent',
          color: add ? 'var(--green-fg)' : del ? 'var(--rose-fg)' : hunk ? 'var(--text-4)' : 'var(--text-2)' }}>{l || ' '}</div>
      );
    })}
    {truncated && <div style={{ padding: '4px 8px', color: 'var(--text-4)', background: 'var(--surface-2)' }}>Truncated</div>}
  </div>
);

const CimDiffDeliverable = ({ x }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <div style={{ borderTop: '1px solid var(--border)', padding: '8px 0' }}>
      <button type="button" disabled={!x.changed} onClick={() => setOpen(!open)} aria-expanded={open}
        style={{ ..._cimRowBtn, padding: 0, cursor: x.changed ? 'pointer' : 'default' }}>
        {x.changed ? <CimChevron open={open} /> : <span style={{ width: 11, flexShrink: 0 }} />}
        <span style={{ fontSize: 11.5, color: 'var(--text-4)', width: 18, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{x.number}</span>
        <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 500, color: x.changed ? 'var(--text)' : 'var(--text-3)' }}>{x.title}</span>
        {x.changed ? (
          <span style={{ fontSize: 11.5, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
            <span style={{ color: 'var(--green-fg)' }}>+{x.added}</span> <span style={{ color: 'var(--rose-fg)' }}>−{x.removed}</span>
          </span>
        ) : <span style={{ fontSize: 11.5, color: 'var(--text-4)', flexShrink: 0 }}>Unchanged</span>}
      </button>
      {open && <CimDiffLines lines={x.diff || []} truncated={x.truncated} />}
    </div>
  );
};

const CimDiffDrawer = ({ state, sectionTitle, folderLabel, onClose, onRef, phone }) => {
  const d = state.data;
  return (
    <window.Drawer open onClose={onClose} width={860} title={`v${d ? d.from_version : state.from} → v${d ? d.to_version : state.to}`}>
      {!d ? <window.SkeletonRows rows={6} /> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div>
            <div style={{ ..._cimCap, marginBottom: 4 }}>Deliverables · {d.deliverables.filter((x) => x.changed).length} changed</div>
            {d.deliverables.map((x) => <CimDiffDeliverable key={x.number} x={x} />)}
          </div>
          <div>
            <div style={{ ..._cimCap, marginBottom: 4 }}>Section plans · {d.sections.length} changed</div>
            {d.sections.length === 0 && <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>No changes.</div>}
            {d.sections.map((s) => (
              <div key={s.section_no} style={{ borderTop: '1px solid var(--border)', padding: '10px 0' }}>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>
                  {_cimPad(s.section_no)} · {sectionTitle(s.section_no) || 'Section'}
                </div>
                {s.fields.map((f) => (
                  <div key={f} style={{ marginBottom: 10 }}>
                    <div style={{ ..._cimCap, marginBottom: 4 }}>{CIM_HANDOFF_LABELS[f] || _cimHuman(f)}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: phone ? '1fr' : '1fr 1fr', gap: 8 }}>
                      {[['Before', s.before && s.before[f], 'var(--rose-bg)', s.display && s.display.before],
                        ['After', s.after && s.after[f], 'var(--green-bg)', s.display && s.display.after]].map(([label, val, bg, disp]) => (
                        <div key={label} style={{ background: bg, borderRadius: 6, padding: '6px 8px', fontSize: 12, color: 'var(--text)', minWidth: 0 }}>
                          <div style={{ fontSize: 10.5, color: 'var(--text-3)', marginBottom: 2 }}>{label}</div>
                          {disp && CIM_REF_FIELDS.has(f) ? <CimRefField f={f} disp={disp} onRef={onRef} />
                            : f === 'external_chapter' ? <span>{_cimChapter(val).label}</span>
                            : f === 'draft_readiness' ? <CimPill map={CIM_READY} k={val} />
                              : f === 'atlas_folders' && val && typeof val === 'object'
                                ? <CimValue v={{ primary: (val.primary || []).map(folderLabel), supporting: (val.supporting || []).map(folderLabel) }} />
                                : <CimValue v={val} />}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </window.Drawer>
  );
};

const CimLaunchDrawer = ({ engagementId, mode, baseRunId, title, mandate, onClose, onStarted }) => {
  const { useState, useEffect } = React;
  const [inputs, setInputs] = useState(null);
  const [err, setErr] = useState('');
  const [web, setWeb] = useState(true);
  const [engaged, setEngaged] = useState(false);
  const [instructions, setInstructions] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let on = true;
    window.AtlasApi.cimInputs(engagementId)
      .then((r) => { if (on) setInputs(r); })
      .catch((e) => { if (on) setErr(_cimErr(e).detail); });
    return () => { on = false; };
  }, [engagementId]);
  const notRecorded = ((inputs && inputs.mandate) || mandate) === 'not_recorded';

  const start = async () => {
    setBusy(true);
    try {
      const res = await window.AtlasApi.startCimRun(engagementId, {
        mode, base_run_id: mode === 'refresh' ? baseRunId : null, web_research: web,
        instructions: instructions.trim() || null, mandate_confirmed: notRecorded && engaged,
      });
      window.pushToast && window.pushToast({ type: 'success', title: `Architecture v${res.run.version} started` });
      onStarted(res);
    } catch (e) {
      const er = _cimErr(e);
      window.pushToast && window.pushToast({ type: 'error', title: er.status === 409 ? 'A run is already going' : 'Could not start the run', body: er.status === 409 ? undefined : er.detail });
      setBusy(false);
      if (er.status === 409) onStarted(null);
    }
  };

  const row = (label, value, warn) => (
    <div style={{ display: 'flex', gap: 10, padding: '6px 0', borderTop: '1px solid var(--border)', fontSize: 12.5, lineHeight: 1.45 }}>
      <span style={{ width: 104, flexShrink: 0, color: 'var(--text-4)' }}>{label}</span>
      <span style={{ flex: 1, minWidth: 0, color: warn ? 'var(--amber-fg)' : 'var(--text-2)', overflowWrap: 'anywhere' }}>{value}</span>
    </div>
  );
  const vlt = (inputs && inputs.vault) || {};
  const room = (inputs && inputs.data_room) || {};
  const check = (checked, onChange, label) => (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text)', cursor: 'pointer' }}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} style={{ width: 15, height: 15, accentColor: 'var(--accent)' }} />
      {label}
    </label>
  );

  return (
    <window.Drawer open onClose={onClose} title={title} width={520}
      footer={(
        <>
          <window.Btn size="sm" variant="outline" disabled={busy} onClick={onClose}>Cancel</window.Btn>
          <window.Btn size="sm" variant="accent" disabled={busy || !inputs} icon={<window.Icon.Play size={12} />} onClick={start}>
            {busy ? 'Starting…' : 'Start run'}
          </window.Btn>
        </>
      )}>
      {err ? <div style={{ fontSize: 12.5, color: 'var(--rose-fg)' }}>{err}</div>
        : !inputs ? <window.SkeletonRows rows={5} />
          : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <div style={{ ..._cimCap, marginBottom: 4 }}>Inputs</div>
                {row('Notes', `${_cimPlural(inputs.notes || 0, 'note')} · ${_cimPlural(inputs.transcripts || 0, 'transcript')}`)}
                {row('Data vault', [`${_cimPlural(vlt.total || 0, 'file')}`, `${vlt.readable || 0} readable`,
                  vlt.unread ? `${vlt.unread} still being read` : null, `${vlt.ready || 0} Ready for CIM`].filter(Boolean).join(' · '))}
                {row('Data room', `${_cimPlural(room.items || 0, 'item')} · ${room.sent || 0} sent`)}
                {row('Website', inputs.website || 'None', !inputs.website)}
                {row('Prior version', inputs.prior ? `v${inputs.prior.version}` : 'None')}
              </div>
              {(inputs.warnings || []).length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {inputs.warnings.map((w, i) => (
                    <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12.5, color: 'var(--amber-fg)', background: 'var(--amber-bg)', borderRadius: 6, padding: '6px 9px' }}>
                      <span aria-hidden style={{ fontWeight: 700, lineHeight: 1.45 }}>!</span>
                      <span style={{ lineHeight: 1.45 }}>{w}</span>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {check(web, setWeb, 'Web research')}
                {notRecorded && check(engaged, setEngaged, 'Owner has engaged us')}
              </div>
              <label style={{ display: 'block' }}>
                <div style={{ ..._cimCap, marginBottom: 4 }}>Instructions</div>
                <textarea value={instructions} rows={4} maxLength={4000} onChange={(e) => setInstructions(e.target.value)}
                  placeholder="Optional" style={{ ..._cimInput, resize: 'vertical' }} />
              </label>
            </div>
          )}
    </window.Drawer>
  );
};

// ── Overview ───────────────────────────────────────────────────────────────

const CimOverviewTab = ({ ov, phone, goTab }) => {
  const e = ov.engagement || {};
  const drivers = [...(ov.drivers || [])].sort(_cimByRank).slice(0, 5);
  const killers = [...(ov.deal_killers || [])].sort(_cimByRank);
  const room = ov.data_room || {};
  const rs = room.summary || {};
  const hover = _cimHover(!phone);
  const linkBtn = (label, tab) => (
    <window.Btn size="sm" variant="ghost" onClick={() => goTab(tab)}>{label} ›</window.Btn>
  );

  const nextPanel = (
    <CimPanel title="Next actions" count={(ov.next_actions || []).length || null}>
      {(ov.next_actions || []).length === 0 ? <CimEmptyLine>Nothing waiting.</CimEmptyLine> : ov.next_actions.map((a, i) => {
        const Ico = window.Icon[CIM_NEXT_ICON[a.key]] || window.Icon.Arrow;
        return (
          <button key={a.key} type="button" {...hover} onClick={() => goTab(a.tab, { filter: a.filter, sub: a.sub })}
            style={{ ..._cimRowBtn, padding: '10px 14px', borderTop: i ? '1px solid var(--border)' : 'none', minHeight: phone ? 48 : 0 }}>
            <Ico size={13} color="var(--accent-text)" />
            <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 500 }}>{a.label}</span>
            <CimChevron />
          </button>
        );
      })}
    </CimPanel>
  );

  const criticalPanel = (
    <CimPanel title="Essential items open" count={(ov.critical || []).length}>
      {(ov.critical || []).length === 0 ? <CimEmptyLine>None.</CimEmptyLine> : ov.critical.map((c, i) => (
        <button key={c.id} type="button" {...hover} onClick={() => goTab('room')}
          style={{ ..._cimRowBtn, padding: '8px 14px', borderTop: i ? '1px solid var(--border)' : 'none' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12.5, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.title}</div>
            <div style={{ fontSize: 11.5, color: 'var(--text-4)' }}>{c.folder}</div>
          </div>
          <window.Pill hue={CIM_ITEM_HUE[c.status] || 'slate'} sz="sm">{c.status_label}</window.Pill>
        </button>
      ))}
    </CimPanel>
  );

  const activityPanel = (
    <CimPanel title="Recent activity">
      {(ov.activity || []).length === 0 ? <CimEmptyLine>No activity yet.</CimEmptyLine> : ov.activity.map((a, i) => (
        <button key={i} type="button" {...hover} onClick={() => a.tab && goTab(a.tab)}
          style={{ ..._cimRowBtn, alignItems: 'baseline', padding: '7px 14px', borderTop: i ? '1px solid var(--border)' : 'none' }}>
          <span style={{ fontSize: 11.5, color: 'var(--text-4)', flexShrink: 0, width: phone ? 92 : 110, fontVariantNumeric: 'tabular-nums' }}>{_cimWhen(a.at)}</span>
          <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: 'var(--text-2)', overflowWrap: 'anywhere' }}>{a.text}</span>
        </button>
      ))}
    </CimPanel>
  );

  const thesisPanel = (
    <CimPanel title="Investment thesis" action={linkBtn('Company intelligence', 'intelligence')}>
      {drivers.length === 0 && killers.length === 0 ? <CimEmptyLine>No architecture yet.</CimEmptyLine> : (
        <div style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {drivers.length > 0 && (
            <div>
              <div style={{ ..._cimCap, marginBottom: 6 }}>Drivers</div>
              {drivers.map((d, i) => (
                <div key={d.key || i} style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '3px 0' }}>
                  <span style={{ fontSize: 11.5, color: 'var(--text-4)', width: 14, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{d.rank || i + 1}</span>
                  <span style={{ fontSize: 12.5, color: 'var(--text)', flex: 1, minWidth: 0 }}>{d.title}</span>
                </div>
              ))}
            </div>
          )}
          {killers.length > 0 && (
            <div>
              <div style={{ ..._cimCap, marginBottom: 6 }}>Deal killers</div>
              {killers.map((k, i) => (
                <div key={k.key || i} style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '3px 0' }}>
                  <span aria-hidden style={{ width: 6, height: 6, borderRadius: 999, background: 'var(--rose-fg)', flexShrink: 0, transform: 'translateY(-1px)' }} />
                  <span style={{ fontSize: 12.5, color: 'var(--text)', flex: 1, minWidth: 0 }}>{k.title}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </CimPanel>
  );

  const roomPanel = (
    <CimPanel title="Data room" action={linkBtn('Open', 'room')}>
      <div style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12.5, color: 'var(--text-2)' }}>
        <window.Pill hue={room.status === 'open' ? 'green' : room.status === 'closed' ? 'slate' : 'amber'} dot sz="sm">
          {room.status === 'open' ? 'Open' : room.status === 'closed' ? 'Closed' : room.status === 'draft' ? 'Draft' : 'Not started'}
        </window.Pill>
        {room.status && (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {[`${rs.accepted || 0}/${rs.total || 0} accepted`, rs.waiting_on_owner ? `${rs.waiting_on_owner} waiting on owner` : null,
              rs.with_us ? `${rs.with_us} with us` : null, rs.drafts ? `${rs.drafts} to approve` : null,
              _cimPlural(room.people || 0, 'person', 'people')].filter(Boolean).join(' · ')}
          </span>
        )}
      </div>
    </CimPanel>
  );

  const ownersPanel = (
    <CimPanel title="Owners" count={(e.owners || []).length || null}>
      {(e.owners || []).length === 0 ? <CimEmptyLine>No contacts on the company.</CimEmptyLine> : (
        <div style={{ padding: '6px 14px 10px' }}>
          {e.owners.map((o, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '4px 0', fontSize: 12.5 }}>
              <span style={{ fontWeight: 500, color: 'var(--text)' }}>{o.name || '—'}</span>
              {o.title && <span style={{ color: 'var(--text-4)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.title}</span>}
            </div>
          ))}
        </div>
      )}
    </CimPanel>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {(ov.path || []).length > 0 && <CimPath steps={ov.path} phone={phone} goTab={goTab} />}
      <div style={{ display: 'grid', gap: 12, alignItems: 'start', gridTemplateColumns: phone ? 'minmax(0, 1fr)' : 'minmax(0, 1.5fr) minmax(0, 1fr)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
          {nextPanel}
          {criticalPanel}
          {activityPanel}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
          {thesisPanel}
          {roomPanel}
          {ownersPanel}
        </div>
      </div>
    </div>
  );
};

// ── Vault ──────────────────────────────────────────────────────────────────

const CimVaultScope = ({ companyId, children }) => {
  const v = window.useVault(companyId);
  return children(v);
};

const CimVaultTab = ({ ov, v, companyId, phone, onClosed }) => {
  const [at, setAt] = React.useState(null);   // null = closed, '' = all files, else a folder key
  const tiles = (ov.vault || []).filter((t) => t.key !== 'to_sort' || t.total > 0);
  const total = tiles.reduce((n, t) => n + (t.total || 0), 0);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{_cimPlural(total, 'file')}</span>
        <div style={{ flex: 1 }} />
        {v && (
          <window.Btn size="sm" variant="outline" disabled={!!v.busy} icon={<window.Icon.Refresh size={12} />} onClick={async () => { await v.pull(); onClosed(); }}>
            {v.busy === 'pull' ? 'Pulling…' : 'Pull in files'}
          </window.Btn>
        )}
        <window.Btn size="sm" variant="accent" disabled={!v || !window.VaultManager} icon={<window.Icon.Maximize size={12} />} onClick={() => setAt('')}>Open vault</window.Btn>
      </div>
      <div style={{ display: 'grid', gap: 8, gridTemplateColumns: phone ? 'minmax(0, 1fr)' : 'repeat(auto-fill, minmax(250px, 1fr))' }}>
        {tiles.map((t) => {
          const readyEmpty = CIM_DATA_FOLDERS.has(t.key) && t.total > 0 && !t.ready;
          const toSort = t.key === 'to_sort';
          return (
            <button key={t.key} type="button" disabled={!v || !window.VaultManager} onClick={() => setAt(t.key)}
              style={{ ..._cimCard, display: 'flex', flexDirection: 'column', gap: 6, padding: '10px 12px', textAlign: 'left', cursor: 'pointer', color: 'var(--text)', minWidth: 0,
                borderColor: toSort ? 'var(--amber-fg)' : 'var(--border)', background: toSort ? 'var(--amber-bg)' : 'var(--surface)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                <window.Icon.Folder size={14} color={toSort ? 'var(--amber-fg)' : 'var(--text-3)'} />
                <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 600, lineHeight: 1.3 }}>{t.label}</span>
                <span style={{ fontSize: 12, color: t.total ? 'var(--text-2)' : 'var(--text-4)', fontVariantNumeric: 'tabular-nums' }}>{t.total}</span>
              </div>
              {t.total > 0 ? (
                <div style={{ fontSize: 11.5, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums', lineHeight: 1.45 }}>
                  {!toSort && <span style={{ color: t.ready ? 'var(--green-fg)' : 'var(--text-4)' }}>{t.ready} Ready for CIM · </span>}
                  {[`${t.readable} readable`, t.unread ? `${t.unread} being read` : null, t.unreadable ? `${t.unreadable} unreadable` : null].filter(Boolean).join(' · ')}
                </div>
              ) : <div style={{ fontSize: 11.5, color: 'var(--text-4)' }}>Empty</div>}
              {(readyEmpty || t.new_since_curated > 0) && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {readyEmpty && <window.Pill hue="amber" sz="sm">Ready is empty</window.Pill>}
                  {t.new_since_curated > 0 && <window.Pill hue="blue" sz="sm">{t.new_since_curated} new since last curated</window.Pill>}
                </div>
              )}
            </button>
          );
        })}
      </div>
      {at !== null && v && window.VaultManager && (
        <window.VaultManager v={v} companyId={companyId} initialFolder={at || undefined} phone={phone}
          onClose={() => { setAt(null); onClosed(); }} />
      )}
    </div>
  );
};

// ── Company intelligence ───────────────────────────────────────────────────

const CimLivePanel = ({ live, canCancel, onCancel, phone }) => {
  const logRef = React.useRef(null);
  const run = live.run || {};
  const events = live.events || [];
  React.useEffect(() => {
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [events.length]);
  const started = run.started_at || run.created_at;
  return (
    <section style={{ ..._cimCard, borderColor: 'var(--accent)' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
        <h3 style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text)', margin: 0 }}>Architecture{run.version ? ` v${run.version}` : ''}</h3>
        <CimPill map={CIM_RUN_STATUS} k={run.status || 'queued'} dot />
        {started && <span style={{ fontSize: 12, color: 'var(--text-3)' }}>Started {_cimWhen(started)}</span>}
        <div style={{ flex: 1 }} />
        {canCancel && <window.Btn size="sm" variant="outline" onClick={onCancel}>Cancel</window.Btn>}
      </header>
      <div ref={logRef} role="log" aria-live="polite"
        style={{ maxHeight: phone ? 300 : 340, overflowY: 'auto', padding: '6px 0', fontSize: 12, lineHeight: 1.5 }}>
        {events.length === 0 ? <div style={{ padding: '4px 14px', color: 'var(--text-4)' }}>Waiting to start</div> : events.map((ev) => {
          const [label, color] = CIM_EVENT[ev.kind] || [_cimHuman(ev.kind), 'var(--text-3)'];
          return (
            <div key={ev.id} style={{ display: 'grid', gridTemplateColumns: phone ? '72px 1fr' : '88px 52px 1fr', gap: 8, padding: '1px 14px' }}>
              <span style={{ color: 'var(--text-4)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{_cimClock(ev.at)}</span>
              {!phone && <span style={{ color, fontWeight: 500 }}>{label}</span>}
              <span style={{ color: ev.kind === 'status' ? 'var(--text)' : 'var(--text-2)', overflowWrap: 'anywhere', minWidth: 0 }}>{ev.text}</span>
            </div>
          );
        })}
      </div>
    </section>
  );
};

const CimDriverCard = ({ d, n }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <div style={{ ..._cimCard, padding: '10px 12px' }}>
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} style={{ ..._cimRowBtn, alignItems: 'flex-start', padding: 0 }}>
        <span style={{ width: 22, height: 22, borderRadius: 999, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          background: 'var(--accent-soft)', color: 'var(--accent-text)', fontSize: 11.5, fontWeight: 650, fontVariantNumeric: 'tabular-nums' }}>{d.rank || n}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{d.title}</span>
            {d.confidence && <CimPill map={CIM_CONFIDENCE} k={d.confidence} />}
            {d.priority && <window.Pill hue={d.priority === 'P0' ? 'violet' : 'slate'} sz="sm">{d.priority}</window.Pill>}
          </div>
          {d.proposition && <div style={{ fontSize: 12.5, color: 'var(--text-2)', marginTop: 4, lineHeight: 1.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{d.proposition}</div>}
        </div>
        <span style={{ paddingTop: 5 }}><CimChevron open={open} /></span>
      </button>
      {open && <CimFields row={d} fields={CIM_DRIVER_FIELDS} />}
    </div>
  );
};

const CimKillerCard = ({ k }) => {
  const [open, setOpen] = React.useState(false);
  const extra = k.assessable_without_management != null
    ? [...CIM_KILLER_FIELDS, ['assessable_without_management', 'Assessable without management']] : CIM_KILLER_FIELDS;
  return (
    <div style={{ ..._cimCard, padding: '10px 12px', borderLeft: '3px solid var(--rose-fg)' }}>
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} style={{ ..._cimRowBtn, alignItems: 'flex-start', padding: 0 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{k.title}</span>
            {k.priority && <window.Pill hue={k.priority === 'P0' ? 'violet' : 'slate'} sz="sm">{k.priority}</window.Pill>}
          </div>
          {k.concern && <div style={{ fontSize: 12.5, color: 'var(--text-2)', marginTop: 4, lineHeight: 1.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{k.concern}</div>}
          {k.mitigants && (
            <div style={{ fontSize: 12.5, color: 'var(--text-2)', marginTop: 6, lineHeight: 1.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
              <span style={{ ..._cimCap, marginRight: 6 }}>Mitigants</span>{k.mitigants}
            </div>
          )}
        </div>
        <span style={{ paddingTop: 5 }}><CimChevron open={open} /></span>
      </button>
      {open && <CimFields row={k} fields={extra} />}
    </div>
  );
};

const CimFields = ({ row, fields }) => {
  const shown = fields.filter(([key]) => !_cimEmpty(row[key]));
  if (!shown.length) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
      {shown.map(([key, label]) => (
        <div key={key}>
          <div style={{ ..._cimCap, marginBottom: 3 }}>{label}</div>
          {row.display && row.display[key]
            ? <div style={{ fontSize: 12.5, lineHeight: 1.5 }}><CimQuestionRefs rows={row.display[key]} /></div>
            : Array.isArray(row[key]) || typeof row[key] === 'boolean'
              ? <div style={{ fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.5 }}><CimValue v={row[key]} /></div>
              : <CimMd compact>{String(row[key])}</CimMd>}
        </div>
      ))}
    </div>
  );
};

const CimThesis = ({ detail, phone }) => {
  const lists = detail.lists || {};
  const drivers = [...(lists.driver || [])].sort(_cimByRank);
  const killers = [...(lists.deal_killer || [])].sort(_cimByRank);
  return (
    <div style={{ display: 'grid', gap: 16, alignItems: 'start', gridTemplateColumns: phone ? 'minmax(0, 1fr)' : 'minmax(0, 1.25fr) minmax(0, 1fr)' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
        <div style={_cimCap}>Investment drivers · {drivers.length}</div>
        {drivers.length === 0 ? <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>None in this version.</div>
          : drivers.map((d, i) => <CimDriverCard key={d.key || i} d={d} n={i + 1} />)}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
        <div style={_cimCap}>Deal killers · {killers.length}</div>
        {killers.length === 0 ? <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>None in this version.</div>
          : killers.map((k, i) => <CimKillerCard key={k.key || i} k={k} />)}
      </div>
    </div>
  );
};

const CimDeliverableCards = ({ detail, phone, onOpen }) => {
  const byNo = new Map((detail.deliverables || []).map((d) => [d.number, d]));
  return (
    <div style={{ display: 'grid', gap: 8, gridTemplateColumns: phone ? 'minmax(0, 1fr)' : 'repeat(auto-fill, minmax(230px, 1fr))' }}>
      {Array.from({ length: 11 }, (_, i) => i + 1).map((n) => {
        const d = byNo.get(n);
        return (
          <button key={n} type="button" disabled={!d} onClick={() => onOpen(n)}
            style={{ ..._cimCard, display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', textAlign: 'left', minWidth: 0,
              cursor: d ? 'pointer' : 'default', color: 'var(--text)', opacity: d ? 1 : 0.6 }}>
            <span style={{ width: 22, height: 22, borderRadius: 6, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              background: 'var(--surface-2)', color: 'var(--text-3)', fontSize: 11.5, fontWeight: 650, fontVariantNumeric: 'tabular-nums' }}>{n}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.35 }}>{d ? d.title : CIM_DELIVERABLES[n]}</div>
              <div style={{ marginTop: 6, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                {d ? <CimPill map={CIM_REVIEW} k={d.review_status} /> : <window.Pill sz="sm">Not in this version</window.Pill>}
                {d && d.review_status !== 'draft' && d.reviewed_by_name && <span style={{ fontSize: 11, color: 'var(--text-4)' }}>{d.reviewed_by_name}</span>}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
};

const CimHandoffTable = ({ detail, phone, onOpen }) => {
  const rows = detail.handoffs || [];
  if (!rows.length) return <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>No section plans in this version.</div>;
  const COLS = '36px minmax(170px, 1.3fr) minmax(150px, 1.1fr) 140px 64px 100px';
  const hover = _cimHover(!phone);
  const p0 = (r) => (r.question_plan || []).filter((q) => q && q.priority === 'P0').length;
  if (phone) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {rows.map((h) => {
          const r = h.record || {};
          const ch = _cimChapter(r.external_chapter);
          return (
            <button key={h.section_no} type="button" onClick={() => onOpen(h.section_no)}
              style={{ ..._cimCard, display: 'flex', flexDirection: 'column', gap: 6, padding: '10px 12px', textAlign: 'left', color: 'var(--text)', cursor: 'pointer' }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', width: '100%' }}>
                <span style={{ fontSize: 12, color: 'var(--text-4)', paddingTop: 1 }}>{_cimPad(h.section_no)}</span>
                <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, lineHeight: 1.3 }}>{r.internal_title}</span>
                <CimPill map={CIM_REVIEW} k={h.review_status} />
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 12, color: 'var(--text-3)' }}>
                {ch.suppressed ? <window.Pill hue="slate" sz="sm">Suppressed</window.Pill> : <span>{ch.label}</span>}
                <CimPill map={CIM_READY} k={r.draft_readiness} />
                <span>{p0(r)} P0</span>
              </div>
            </button>
          );
        })}
      </div>
    );
  }
  const head = (label, align) => <span style={{ ..._cimCap, textAlign: align || 'left' }}>{label}</span>;
  return (
    <div style={{ ..._cimCard, overflowX: 'auto' }}>
      <div style={{ minWidth: 720 }}>
        <div style={{ display: 'grid', gridTemplateColumns: COLS, gap: 8, alignItems: 'center', padding: '8px 14px', borderBottom: '1px solid var(--border)' }}>
          {head('§')}{head('Section')}{head('External chapter')}{head('Readiness')}{head('P0', 'right')}{head('Review')}
        </div>
        {rows.map((h, i) => {
          const r = h.record || {};
          const ch = _cimChapter(r.external_chapter);
          return (
            <div key={h.section_no} role="button" tabIndex={0} {...hover} onClick={() => onOpen(h.section_no)}
              onKeyDown={(e) => { if (e.key === 'Enter') onOpen(h.section_no); }}
              style={{ display: 'grid', gridTemplateColumns: COLS, gap: 8, alignItems: 'center', padding: '8px 14px', borderTop: i ? '1px solid var(--border)' : 'none', cursor: 'pointer' }}>
              <span style={{ fontSize: 12, color: 'var(--text-4)', fontVariantNumeric: 'tabular-nums' }}>{_cimPad(h.section_no)}</span>
              <span style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.internal_title}>{r.internal_title}</span>
              <span style={{ minWidth: 0, display: 'flex' }}>
                {ch.suppressed ? <window.Pill hue="slate" sz="sm">Suppressed</window.Pill>
                  : <span style={{ fontSize: 12, color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={ch.label}>{ch.label}</span>}
              </span>
              <div><CimPill map={CIM_READY} k={r.draft_readiness} /></div>
              <span style={{ fontSize: 12, textAlign: 'right', color: p0(r) ? 'var(--violet-fg)' : 'var(--text-4)', fontVariantNumeric: 'tabular-nums' }}>{p0(r)}</span>
              <div><CimPill map={CIM_REVIEW} k={h.review_status} /></div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const CimQuestionnaire = ({ detail, sectionTitle, folderLabel, canAsk, busy, onAsk, phone }) => {
  const { useState, useMemo } = React;
  const [prio, setPrio] = useState('all');
  const [sel, setSel] = useState(() => new Set());
  const all = (detail.lists && detail.lists.question) || [];
  const qid = (q) => q.key || q.id;
  const reqs = useMemo(() => new Map((detail.requests || []).map((r) => [r.key, r])), [detail.requests]);
  const reqOf = (q) => reqs.get(`q-${String(qid(q)).replace(/\./g, '-')}`);
  const sorted = useMemo(() => [...all].sort((a, b) => {
    const x = _cimQid(qid(a)); const y = _cimQid(qid(b));
    return x[0] - y[0] || x[1] - y[1];
  }), [all]);
  const shown = sorted.filter((q) => prio === 'all' || q.priority === prio);
  const groups = [];
  shown.forEach((q) => {
    const sid = q.section_id || _cimPad(_cimQid(qid(q))[0]);
    const g = groups[groups.length - 1];
    if (g && g.sid === sid) g.items.push(q); else groups.push({ sid, items: [q] });
  });
  const toggle = (id) => setSel((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleGroup = (items, on) => setSel((s) => { const n = new Set(s); items.forEach((q) => { if (on) n.add(qid(q)); else n.delete(qid(q)); }); return n; });
  const reqPill = (r) => (r.dismissed ? <window.Pill sz="sm">Dismissed</window.Pill>
    : r.sent ? <window.Pill hue="green" sz="sm">Sent</window.Pill>
      : r.draft ? <window.Pill hue="amber" sz="sm">To approve</window.Pill> : <window.Pill sz="sm">In data room</window.Pill>);
  const counts = { all: all.length, P0: 0, P1: 0, P2: 0 };
  all.forEach((q) => { if (counts[q.priority] != null) counts[q.priority] += 1; });

  if (!all.length) return <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>No questionnaire in this version.</div>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ position: 'sticky', top: 0, zIndex: 2, background: 'var(--bg, var(--surface))', padding: '4px 0', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        {['all', 'P0', 'P1', 'P2'].map((k) => {
          const on = prio === k;
          return (
            <button key={k} type="button" onClick={() => setPrio(k)}
              style={{ flexShrink: 0, padding: '4px 10px', borderRadius: 999, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap',
                border: `1px solid ${on ? 'var(--accent)' : 'var(--border)'}`, background: on ? 'var(--accent-soft)' : 'var(--surface)',
                color: on ? 'var(--accent-text)' : 'var(--text-2)' }}>
              {k === 'all' ? 'All' : `${k} · ${CIM_PRIORITY[k]}`} <span style={{ color: 'var(--text-4)', fontVariantNumeric: 'tabular-nums' }}>{counts[k]}</span>
            </button>
          );
        })}
        <div style={{ flex: 1 }} />
        {canAsk && sel.size > 0 && (
          <>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--accent-text)' }}>{sel.size} selected</span>
            <window.Btn size="sm" variant="ghost" onClick={() => setSel(new Set())}>Clear</window.Btn>
            <window.Btn size="sm" variant="accent" disabled={busy} icon={<window.Icon.Mail size={12} />}
              onClick={async () => { if (await onAsk([...sel])) setSel(new Set()); }}>Ask the owner</window.Btn>
          </>
        )}
      </div>
      {groups.length === 0 && <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>No questions match.</div>}
      {groups.map((g) => {
        const allOn = g.items.every((q) => sel.has(qid(q)));
        return (
          <div key={g.sid} style={{ ..._cimCard, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', background: 'var(--surface-2)', borderBottom: '1px solid var(--border)' }}>
              {canAsk && (
                <input type="checkbox" checked={allOn} onChange={() => toggleGroup(g.items, !allOn)} aria-label={`Select section ${g.sid}`}
                  style={{ width: 14, height: 14, cursor: 'pointer', accentColor: 'var(--accent)' }} />
              )}
              <span style={{ fontSize: 12, color: 'var(--text-4)', fontVariantNumeric: 'tabular-nums' }}>{g.sid}</span>
              <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>{sectionTitle(g.sid)}</span>
              <span style={{ fontSize: 11.5, color: 'var(--text-4)', fontVariantNumeric: 'tabular-nums' }}>{g.items.length}</span>
            </div>
            {g.items.map((q, i) => {
              const id = qid(q);
              const r = reqOf(q);
              const meta = [q.response_type ? (CIM_RESPONSE[q.response_type] || _cimHuman(q.response_type)) : null,
                q.tier ? `Tier ${q.tier}` : null, q.likely_source ? `Source: ${q.likely_source}` : null,
                q.vault_folder ? folderLabel(q.vault_folder) : null].filter(Boolean).join(' · ');
              return (
                <label key={id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: phone ? '10px 14px' : '8px 14px', borderTop: i ? '1px solid var(--border)' : 'none', cursor: canAsk ? 'pointer' : 'default' }}>
                  {canAsk && (
                    <input type="checkbox" checked={sel.has(id)} onChange={() => toggle(id)}
                      style={{ width: 14, height: 14, marginTop: 2, cursor: 'pointer', accentColor: 'var(--accent)', flexShrink: 0 }} />
                  )}
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--text-4)', flexShrink: 0, minWidth: 36, paddingTop: 1 }}>{id}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: phone ? 14 : 12.5, color: 'var(--text)', lineHeight: 1.45, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{q.question}</div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginTop: 4 }}>
                      {q.priority && <window.Pill hue={q.priority === 'P0' ? 'violet' : 'slate'} sz="sm">{q.priority}</window.Pill>}
                      {r && reqPill(r)}
                      {meta && <span style={{ fontSize: 11.5, color: 'var(--text-4)', overflowWrap: 'anywhere' }}>{meta}</span>}
                    </div>
                    {q.evidence_needed && <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 3, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{q.evidence_needed}</div>}
                  </div>
                </label>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

const CimVersions = ({ runs, selId, currentId, onSelect, canShowAll, onShowAll, phone }) => (
  <div style={{ ..._cimCard, overflow: 'hidden' }}>
    {runs.map((r, i) => {
      const on = r.id === selId;
      const active = CIM_ACTIVE.has(r.status);
      const when = r.finished_at || r.started_at || r.created_at;
      return (
        <button key={r.id} type="button" disabled={active} onClick={() => onSelect(r.id)} aria-current={on ? 'true' : undefined}
          style={{ ..._cimRowBtn, flexWrap: phone ? 'wrap' : 'nowrap', gap: 8, padding: '9px 14px', borderTop: i ? '1px solid var(--border)' : 'none',
            background: on ? 'var(--accent-soft)' : 'transparent', boxShadow: on ? 'inset 2px 0 0 var(--accent)' : 'none', cursor: active ? 'default' : 'pointer' }}>
          <span style={{ fontSize: 13, fontWeight: 650, width: 34, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>v{r.version}</span>
          <CimPill map={CIM_RUN_STATUS} k={r.status} />
          <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{r.mode === 'refresh' ? 'Refresh' : 'Full run'}</span>
          {r.id === currentId && <window.Pill hue="blue" sz="sm">Current</window.Pill>}
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 12, color: 'var(--text-4)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {[r.requested_by_name, _cimWhen(when)].filter(Boolean).join(' · ')}
          </span>
        </button>
      );
    })}
    {canShowAll && (
      <div style={{ padding: '6px 8px', borderTop: '1px solid var(--border)' }}>
        <window.Btn size="sm" variant="ghost" onClick={onShowAll}>Show all versions</window.Btn>
      </div>
    )}
  </div>
);

const CIM_SUBS = [
  { key: 'thesis', label: 'Investment thesis' },
  { key: 'deliverables', label: 'Deliverables' },
  { key: 'handoffs', label: 'Section plans' },
  { key: 'questions', label: 'Questionnaire' },
  { key: 'versions', label: 'Versions' },
];

const CimIntelTab = ({
  ov, runs, sel, detail, live, phone, busy, sectionTitle, folderLabel, sub, setSub,
  onLaunch, onCancel, onSelect, onCompare, prevOf, onOpenReader, onAsk, canShowAll, onShowAll,
}) => {
  const canStart = ov.can_run && ov.enabled && !live && !ov.active_run_id;
  const hasCompleted = !!ov.current_run_id;
  const latest = runs[0] || null;
  const latestFailed = !live && latest && latest.status === 'failed' && (!sel || sel.id !== latest.id) ? latest : null;
  const runButtons = canStart && (hasCompleted ? (
    <>
      <window.Btn size="sm" variant="outline" onClick={() => onLaunch('full')}>Run again</window.Btn>
      <window.Btn size="sm" variant="accent" icon={<window.Icon.Refresh size={12} />} onClick={() => onLaunch('refresh')}>Refresh</window.Btn>
    </>
  ) : (
    <window.Btn size="sm" variant="accent" icon={<window.Icon.Play size={12} />} onClick={() => onLaunch('full')}>Run architecture</window.Btn>
  ));
  const offPill = ov.can_run && !ov.enabled ? <window.Pill sz="sm">Runs switched off</window.Pill> : null;
  const prev = sel && sel.status === 'completed' ? prevOf(sel) : null;
  const reviews = (sel && sel.reviews) || { deliverables: {}, handoffs: {} };
  const approvedD = (reviews.deliverables || {}).approved || 0;
  const approvedH = (reviews.handoffs || {}).approved || 0;
  const reviewedN = ['deliverables', 'handoffs'].reduce((n, k) => n + Object.entries(reviews[k] || {}).filter(([s]) => s !== 'draft').reduce((a, [, x]) => a + x, 0), 0);
  const ready = detail && detail.run && sel && detail.run.id === sel.id;
  const errorBox = (r) => (
    <div style={{ borderLeft: '3px solid var(--rose-fg)', background: 'var(--rose-bg)', borderRadius: 6, padding: '8px 10px', display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--rose-fg)' }}>v{r.version} failed{r.finished_at ? ` · ${_cimWhen(r.finished_at)}` : ''}</div>
        {r.error && <div style={{ fontSize: 12.5, color: 'var(--text)', marginTop: 2, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{r.error}</div>}
      </div>
      {canStart && <window.Btn size="sm" variant="outline" onClick={() => onLaunch('full')}>Run again</window.Btn>}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {live && <CimLivePanel live={live} phone={phone} canCancel={ov.can_run} onCancel={onCancel} />}
      {latestFailed && errorBox(latestFailed)}

      {!sel ? (
        !live && (
          <div style={{ ..._cimCard, padding: '28px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, textAlign: 'center' }}>
            <window.Icon.Sparkle size={20} color="var(--text-3)" />
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>No architecture yet</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>{runButtons}{offPill}</div>
          </div>
        )
      ) : (
        <section style={{ ..._cimCard, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <h2 style={{ fontSize: 15, fontWeight: 650, color: 'var(--text)', margin: 0 }}>Architecture v{sel.version}</h2>
            <CimPill map={CIM_RUN_STATUS} k={sel.status} dot />
            {sel.mode === 'refresh' && <window.Pill sz="sm">Refresh</window.Pill>}
            {sel.id !== ov.current_run_id && ov.current_run_id && (
              <window.Btn size="sm" variant="ghost" onClick={() => onSelect(ov.current_run_id)}>Show current</window.Btn>
            )}
            <div style={{ flex: 1 }} />
            {prev && (
              <window.Btn size="sm" variant="ghost" icon={<window.Icon.Layers size={12} />} onClick={() => onCompare(sel, prev)}>Compare with v{prev.version}</window.Btn>
            )}
            {runButtons}
            {offPill}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-3)', display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {[sel.skill_version ? `Skill ${sel.skill_version}` : null,
              sel.saved_at ? `Saved ${_cimWhen(sel.saved_at)}` : (sel.finished_at ? `Finished ${_cimWhen(sel.finished_at)}` : null),
              sel.requested_by_name, sel.web_research === false ? 'No web research' : null,
              sel.mandate_basis === 'not_recorded' ? 'No mandate recorded' : null].filter(Boolean).join(' · ')}
            {sel.atlas_read_status && sel.atlas_read_status !== 'available' && (
              <span style={{ color: 'var(--amber-fg)' }}> · Atlas read {sel.atlas_read_status}</span>
            )}
          </div>
          {sel.status === 'completed' && (
            <div style={{ fontSize: 12, color: 'var(--text-2)', fontVariantNumeric: 'tabular-nums' }}>
              Deliverables {approvedD}/11 approved · Handoffs {approvedH}/17 approved · {reviewedN} of 28 reviewed
            </div>
          )}
          {sel.instructions && (
            <div style={{ fontSize: 12, color: 'var(--text-2)', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
              <span style={{ ..._cimCap, marginRight: 6 }}>Instructions</span>{sel.instructions}
            </div>
          )}
          {sel.status === 'failed' && errorBox(sel)}
          {sel.change_summary && sel.status === 'completed' && (
            <details style={{ borderTop: '1px solid var(--border)', paddingTop: 8 }}>
              <summary style={{ cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>Change summary</summary>
              <div style={{ marginTop: 6 }}><CimMd compact>{sel.change_summary}</CimMd></div>
            </details>
          )}
        </section>
      )}

      {runs.some((r) => !CIM_ACTIVE.has(r.status)) && (
        <>
          <div style={{ display: 'flex', gap: phone ? 6 : 2, overflowX: phone ? 'auto' : 'visible', borderBottom: phone ? 'none' : '1px solid var(--border)', flexWrap: 'nowrap' }}>
            {CIM_SUBS.map((s) => {
              const on = sub === s.key;
              return phone ? (
                <button key={s.key} type="button" onClick={() => setSub(s.key)}
                  style={{ flexShrink: 0, padding: '6px 10px', borderRadius: 999, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap',
                    border: `1px solid ${on ? 'var(--accent)' : 'var(--border)'}`, background: on ? 'var(--accent-soft)' : 'var(--surface)',
                    color: on ? 'var(--accent-text)' : 'var(--text-2)' }}>{s.label}</button>
              ) : (
                <button key={s.key} type="button" onClick={() => setSub(s.key)}
                  style={{ padding: '0 10px', height: 30, border: 'none', background: 'transparent', fontSize: 12.5, fontWeight: on ? 600 : 500, cursor: 'pointer', whiteSpace: 'nowrap',
                    color: on ? 'var(--text)' : 'var(--text-3)', borderBottom: on ? '2px solid var(--text)' : '2px solid transparent', marginBottom: -1 }}>{s.label}</button>
              );
            })}
          </div>
          {sub === 'versions' ? (
            <CimVersions runs={runs} selId={sel && sel.id} currentId={ov.current_run_id} onSelect={onSelect} phone={phone}
              canShowAll={canShowAll} onShowAll={onShowAll} />
          ) : !sel || sel.status !== 'completed' ? (
            <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>No output in this version.</div>
          ) : !ready ? (
            detail && detail.error ? <div style={{ fontSize: 12.5, color: 'var(--rose-fg)' }}>{detail.error}</div> : <window.SkeletonRows rows={5} />
          ) : sub === 'thesis' ? (
            <CimThesis detail={detail} phone={phone} />
          ) : sub === 'deliverables' ? (
            <CimDeliverableCards detail={detail} phone={phone} onOpen={(n) => onOpenReader(sel.id, 'deliverable', n)} />
          ) : sub === 'handoffs' ? (
            <CimHandoffTable detail={detail} phone={phone} onOpen={(n) => onOpenReader(sel.id, 'handoff', n)} />
          ) : (
            <CimQuestionnaire key={detail && detail.run ? detail.run.id : 'none'} detail={detail} sectionTitle={sectionTitle} folderLabel={folderLabel} phone={phone}
              canAsk={!!ov.can_run} busy={busy} onAsk={(ids) => onAsk(sel.id, ids)} />
          )}
        </>
      )}
    </div>
  );
};

// ── the page ───────────────────────────────────────────────────────────────

const CimCommandCenter = ({ engagementId, phone: phoneProp }) => {
  const { useState, useEffect, useRef, useCallback, useMemo } = React;
  const device = window.useDevice ? window.useDevice() : null;
  const phone = phoneProp != null ? !!phoneProp : !!(device && device.phone);
  const api = window.AtlasApi;
  const [ov, setOv] = useState(null);
  const [ovErr, setOvErr] = useState(null);
  const [tab, setTab] = useState(_cimTabFromHash);
  const [roomFilter, setRoomFilter] = useState('all');
  const [ledgerSub, setLedgerSub] = useState('statements');
  const [details, setDetails] = useState({});      // run id → full run detail (or {error})
  const [selRunId, setSelRunId] = useState(null);
  const [live, setLive] = useState(null);           // {runId, run, events} for the run in flight
  const [launch, setLaunch] = useState(null);       // {mode, baseRunId, title}
  const [reader, setReader] = useState(null);       // {runId, kind: 'deliverable'|'handoff', key}
  const [refOpen, setRefOpen] = useState(null);     // {kind: 'note'|'file', id} a handoff cites
  const [diff, setDiff] = useState(null);           // {from, to, data}
  const [busy, setBusy] = useState(false);
  const [allRuns, setAllRuns] = useState(null);
  const [intelSub, setIntelSub] = useState('thesis');
  const [ledgerLive, setLedgerLive] = useState(false); // a ledger extraction in flight (the tab's dot)
  const scroller = useRef(null);
  const ovRef = useRef(null);
  ovRef.current = ov;
  const detailsRef = useRef(details);
  detailsRef.current = details;
  const liveRef = useRef(live);
  liveRef.current = live;
  const loadSeq = useRef(0);
  const inflight = useRef(new Set());
  const finished = useRef(new Set());
  const toast = (t) => window.pushToast && window.pushToast(t);

  const loadOv = useCallback(() => {
    const my = ++loadSeq.current;
    return api.cimOverview(engagementId)
      .then((r) => { if (my === loadSeq.current) { setOv(r); setOvErr(null); } return r; })
      .catch((e) => {
        if (my !== loadSeq.current) return null;
        const er = _cimErr(e);
        if (!ovRef.current || er.status === 404 || er.status === 422) { setOv(null); setOvErr(er); }
        else toast({ type: 'error', title: 'Could not refresh', body: er.detail });
        return null;
      });
  }, [engagementId]);

  const fetchDetail = useCallback((id, force = false) => {
    if (!id) return Promise.resolve(null);
    const have = detailsRef.current[id];
    if (!force && have && have.run) return Promise.resolve(have);
    if (inflight.current.has(id)) return Promise.resolve(null);
    inflight.current.add(id);
    return api.cimRun(id)
      .then((r) => { setDetails((d) => ({ ...d, [id]: r })); return r; })
      .catch((e) => { setDetails((d) => (d[id] && d[id].run ? d : { ...d, [id]: { error: _cimErr(e).detail } })); return null; })
      .finally(() => { inflight.current.delete(id); });
  }, []);

  useEffect(() => {
    setOv(null); setOvErr(null); setDetails({}); setSelRunId(null); setLive(null); setAllRuns(null);
    setReader(null); setDiff(null); setLaunch(null); setRefOpen(null);
    finished.current = new Set();
    loadOv();
  }, [engagementId]);

  // Every tab switch re-reads the overview (the data room and the vault
  // change the meters and tiles underneath it).
  const firstTab = useRef(true);
  useEffect(() => {
    if (firstTab.current) { firstTab.current = false; return; }
    loadOv();
    if (scroller.current) scroller.current.scrollTop = 0;
  }, [tab]);

  // The tab in the hash — refine only our own route, never another page's.
  // The same tab keeps its own params (the Sections tab's section and version).
  useEffect(() => {
    const next = _cimTabHash(window.location.hash || '', `#/cim/${engagementId}`, tab);
    if (!next) return;
    try { history.replaceState(null, '', next); } catch (_) { /* ignore */ }
  }, [tab, engagementId]);
  useEffect(() => {
    const onHash = () => {
      if (!(window.location.hash || '').startsWith(`#/cim/${engagementId}`)) return;
      const t = _cimTabFromHash();
      setTab((x) => (x === t ? x : t));
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, [engagementId]);

  const runs = useMemo(() => {
    const head = (ov && ov.runs) || [];
    if (!allRuns) return head;
    const ids = new Set(head.map((r) => r.id));
    return [...head, ...allRuns.filter((r) => !ids.has(r.id))];
  }, [ov, allRuns]);

  // Default selection: the latest completed version, else the newest
  // finished one. A run in flight shows in the live panel instead.
  useEffect(() => {
    if (!ov) return;
    setSelRunId((cur) => {
      if (cur && runs.some((r) => r.id === cur && !CIM_ACTIVE.has(r.status))) return cur;
      return ov.current_run_id || (runs.find((r) => !CIM_ACTIVE.has(r.status)) || {}).id || null;
    });
  }, [ov, runs]);

  useEffect(() => { if (ov) setLedgerLive(!!ov.ledger_job_id); }, [ov]);

  // A run in flight (from the overview — another tab or person may have
  // started it) goes to the live panel.
  useEffect(() => {
    if (!ov || !ov.active_run_id) return;
    const id = ov.active_run_id;
    if (finished.current.has(id)) return;
    setLive((cur) => (cur && cur.runId === id ? cur : { runId: id, run: (ov.runs || []).find((r) => r.id === id) || null, events: [] }));
  }, [ov]);

  const sel = runs.find((r) => r.id === selRunId) || null;
  useEffect(() => {
    if (tab !== 'intelligence' || !sel || sel.status !== 'completed') return;
    fetchDetail(sel.id);
  }, [tab, sel && sel.id, sel && sel.status]);

  const goTab = useCallback((t, opts) => {
    setRoomFilter((opts && opts.filter) || 'all');
    setLedgerSub((opts && opts.sub) || 'statements');
    setTab(t);
  }, []);

  const finish = useCallback((run) => {
    if (!run || finished.current.has(run.id)) return;
    finished.current.add(run.id);
    setLive((cur) => (cur && cur.runId === run.id ? null : cur));
    loadOv();
    if (run.status === 'completed') {
      fetchDetail(run.id, true).then(() => setSelRunId(run.id));
      toast({ type: 'success', title: `Architecture v${run.version} is ready` });
    } else if (run.status === 'failed') {
      toast({ type: 'error', title: `Architecture v${run.version} failed`, body: run.error || undefined });
    } else {
      toast({ type: 'info', title: `Architecture v${run.version} cancelled` });
    }
  }, [loadOv, fetchDetail]);

  // Progress lines every 5 s while a run is in flight; paused while the tab
  // is hidden. Terminal status → reload the overview and the full run.
  window.usePolling(async () => {
    const L = liveRef.current;
    if (!L) return 'idle';
    const last = L.events.length ? L.events[L.events.length - 1].id : 0;
    const r = await api.cimRun(L.runId, { after: last, eventsOnly: true });
    const cur = liveRef.current;
    if (!cur || cur.runId !== L.runId) return 'stale';
    const evs = (r && r.events) || [];
    setLive((c) => (c && c.runId === L.runId ? { ...c, run: r.run || c.run, events: _cimMergeEvents(c.events, evs) } : c));
    if (r && r.run && CIM_TERMINAL.has(r.run.status)) finish(r.run);
    return `${r && r.run ? r.run.status : ''}:${evs.length ? evs[evs.length - 1].id : last}`;
  }, {
    active: !!live, key: live ? live.runId : null, first: 5000, stepAfter: 60 * 60 * 1000,
    giveUpAfter: 0, immediate: true, signature: (s) => s,
  });

  const openLaunch = (mode) => {
    const o = ovRef.current;
    if (!o) return;
    const hasCompleted = !!o.current_run_id;
    setLaunch({
      mode, baseRunId: mode === 'refresh' ? o.current_run_id : null,
      title: mode === 'refresh' ? 'Refresh architecture' : hasCompleted ? 'Run again' : 'Run architecture',
    });
  };
  const onStarted = (res) => {
    setLaunch(null);
    if (res && res.run) {
      setLive({ runId: res.run.id, run: res.run, events: res.events || [] });
      setTab('intelligence');
    }
    loadOv();
  };
  const cancelRun = async () => {
    const L = liveRef.current;
    if (!L) return;
    const ok = await window.showConfirm({
      title: `Cancel architecture${L.run && L.run.version ? ` v${L.run.version}` : ''}?`,
      confirmLabel: 'Cancel run', cancelLabel: 'Keep running', danger: true,
    });
    if (!ok) return;
    try {
      const r = await api.cancelCimRun(L.runId);
      if (r && r.run) finish(r.run);
    } catch (e) {
      _cimToastErr('Could not cancel', e);
      loadOv();
    }
  };

  const doReview = async (runId, kind, key, status, note) => {
    setBusy(true);
    try {
      const r = await api.reviewCim(runId, { kind, key, status, note: note || null });
      if (r && r.run) setDetails((d) => ({ ...d, [runId]: r }));
      toast({ type: 'success', title: `${kind === 'deliverable' ? `Deliverable ${key}` : `Section ${_cimPad(key)} plan`}: ${(CIM_REVIEW[status] || [status])[0]}` });
      loadOv();
      return true;
    } catch (e) {
      _cimToastErr('Could not save the review', e);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const askOwner = async (runId, ids) => {
    setBusy(true);
    try {
      const r = await api.askOwnerCim(runId, ids);
      const x = (r && r.result) || {};
      const skipped = (r && r.skipped) || [];
      const made = x.created || 0;
      const title = made ? `${_cimPlural(made, 'draft')} added to To approve`
        : x.updated ? `${_cimPlural(x.updated, 'draft')} updated in To approve` : 'No new drafts';
      const bits = [];
      if (made && x.updated) bits.push(`${x.updated} updated`);
      if (skipped.length) {
        bits.push(`Skipped ${skipped.slice(0, 3).map((s) => `${s.id} (${s.why})`).join('; ')}${skipped.length > 3 ? ` and ${skipped.length - 3} more` : ''}`);
      }
      toast({
        type: 'success', title, body: bits.join(' · ') || undefined, duration: 9000,
        action: { label: 'Open data room', onClick: () => goTab('room', { filter: 'approve' }) },
      });
      fetchDetail(runId, true);
      loadOv();
      return true;
    } catch (e) {
      _cimToastErr('Could not add the questions', e);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const prevOf = (run) => runs.filter((r) => r.status === 'completed' && r.version < run.version)
    .sort((a, b) => b.version - a.version)[0] || null;
  const openDiff = async (run, other) => {
    setDiff({ from: other.version, to: run.version, data: null });
    try {
      const d = await api.cimDiff(run.id, other.id);
      setDiff((cur) => (cur && cur.to === run.version && cur.from === other.version ? { ...cur, data: d } : cur));
    } catch (e) {
      _cimToastErr('Could not compare versions', e);
      setDiff(null);
    }
  };
  const showAll = () => api.cimRuns(engagementId).then((r) => setAllRuns((r && r.runs) || [])).catch((e) => _cimToastErr('Could not load versions', e));

  const openReader = (runId, kind, key) => {
    setReader({ runId, kind, key });
    fetchDetail(runId);
  };
  // A section's external chapter, from the newest approved handoff (Sections tab).
  const sectionChapter = (sectionId) => {
    const row = ((ov && ov.sections) || []).find((s) => s.section_id === sectionId);
    return row ? _cimChapter(row.external_chapter) : null;
  };

  const sectionTitle = (sid) => {
    const key = _cimPad(parseInt(sid, 10));
    return ((ov && ov.sections) || []).find((s) => s.section_id === key)?.title || '';
  };
  const folderLabel = (k) => (k === 'all' ? 'All folders' : ((ov && ov.vault) || []).find((t) => t.key === k)?.label || _cimHuman(k));

  const wrap = (body) => (
    // The app shell clips its main area; the page scrolls itself.
    <div ref={scroller} style={{ height: '100%', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <div style={{ padding: phone ? '12px 16px 32px' : '18px 22px 40px', maxWidth: 1280, boxSizing: 'border-box' }}>{body}</div>
    </div>
  );

  if (!ov) {
    if (ovErr) {
      return wrap(
        <div style={{ ..._cimCard, padding: '28px 20px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
            {ovErr.status === 404 || ovErr.status === 422 ? ovErr.detail : 'Could not load the CIM Command Center'}
          </div>
          {!(ovErr.status === 404 || ovErr.status === 422) && <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>{ovErr.detail}</div>}
          {ovErr.status !== 404 && ovErr.status !== 422 && <window.Btn size="sm" variant="outline" onClick={() => loadOv()}>Try again</window.Btn>}
        </div>,
      );
    }
    return wrap(<window.SkeletonRows rows={8} />);
  }

  const e = ov.engagement || {};
  const cid = e.company_id;
  const team = [...new Set([e.dealmaker, e.associate, ...(e.team || [])].filter(Boolean))];
  const signed = ov.mandate === 'signed';
  const goDeal = () => window.AtlasNav && window.AtlasNav.goToDeal && window.AtlasNav.goToDeal(e.id);
  const detail = sel ? details[sel.id] : null;
  const readerDetail = reader ? details[reader.runId] : null;

  const header = (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={_cimCap}>CIM</div>
        <h1 style={{ fontSize: phone ? 19 : 20, fontWeight: 650, margin: '2px 0 0', color: 'var(--text)', letterSpacing: '-0.01em', lineHeight: 1.25, overflowWrap: 'anywhere' }}>{e.company_name}</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
          <button type="button" onClick={goDeal} title="Open the deal"
            style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', fontSize: 13, fontWeight: 500, color: 'var(--accent-text)', textAlign: 'left' }}>{e.name}</button>
          {e.stage && <window.Pill hue="slate" dot sz="sm" maxW={220}>{e.stage}</window.Pill>}
          <window.Pill hue={signed ? 'green' : 'amber'} sz="sm">{signed ? 'Mandate signed' : 'No mandate recorded'}</window.Pill>
          {team.length > 0 && <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{team.join(' · ')}</span>}
        </div>
      </div>
      {!phone && <window.Btn size="sm" variant="outline" icon={<window.Icon.External size={12} />} onClick={goDeal}>Open deal</window.Btn>}
    </div>
  );

  const tabs = phone ? (
    <div role="tablist" style={{ display: 'flex', gap: 6, overflowX: 'auto', margin: '12px -16px 0', padding: '0 16px 2px', WebkitOverflowScrolling: 'touch' }}>
      {CIM_TABS.map((t) => {
        const on = tab === t.key;
        return (
          <button key={t.key} type="button" role="tab" aria-selected={on} onClick={() => goTab(t.key)}
            style={{ flexShrink: 0, padding: '7px 12px', borderRadius: 999, fontSize: 13.5, cursor: 'pointer', whiteSpace: 'nowrap',
              border: `1px solid ${on ? 'var(--accent)' : 'var(--border)'}`, background: on ? 'var(--accent-soft)' : 'var(--surface)',
              color: on ? 'var(--accent-text)' : 'var(--text-2)', fontWeight: on ? 600 : 500 }}>
            {t.label}{(t.key === 'intelligence' && live) || (t.key === 'ledger' && ledgerLive) ? ' •' : ''}
          </button>
        );
      })}
    </div>
  ) : (
    <div role="tablist" style={{ display: 'flex', gap: 2, borderBottom: '1px solid var(--border)', marginTop: 12 }}>
      {CIM_TABS.map((t) => {
        const on = tab === t.key;
        return (
          <button key={t.key} type="button" role="tab" aria-selected={on} onClick={() => goTab(t.key)}
            style={{ padding: '0 10px', height: 32, border: 'none', background: 'transparent', fontSize: 12.5, fontWeight: on ? 600 : 500, cursor: 'pointer',
              color: on ? 'var(--text)' : 'var(--text-3)', borderBottom: on ? '2px solid var(--text)' : '2px solid transparent', marginBottom: -1,
              display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {t.label}
            {((t.key === 'intelligence' && live) || (t.key === 'ledger' && ledgerLive))
              && <span aria-label="Running" style={{ width: 6, height: 6, borderRadius: 999, background: 'var(--accent)' }} />}
          </button>
        );
      })}
    </div>
  );

  const vaultBody = (v) => (tab === 'room' ? (
    <div style={{ ..._cimCard, display: 'flex', overflow: 'hidden', height: phone ? 'calc(100dvh - 250px)' : 'calc(100vh - 210px)', minHeight: phone ? 420 : 460 }}>
      {window.DataRoomPane
        ? <window.DataRoomPane key={`room-${roomFilter}`} companyId={cid} v={v} phone={phone} initialFilter={roomFilter} />
        : <CimEmptyLine>The data room isn't available.</CimEmptyLine>}
    </div>
  ) : (
    <CimVaultTab ov={ov} v={v} companyId={cid} phone={phone} onClosed={() => loadOv()} />
  ));

  let body = null;
  if (tab === 'overview') {
    body = <CimOverviewTab ov={ov} phone={phone} goTab={goTab} />;
  } else if (tab === 'room' || tab === 'vault') {
    body = window.useVault
      ? <CimVaultScope companyId={cid}>{vaultBody}</CimVaultScope>
      : <CimEmptyLine>The vault isn't available.</CimEmptyLine>;
  } else if (tab === 'intelligence') {
    body = (
      <CimIntelTab ov={ov} runs={runs} sel={sel} detail={detail} live={live} phone={phone} busy={busy}
        sectionTitle={sectionTitle} folderLabel={folderLabel} sub={intelSub} setSub={setIntelSub}
        onLaunch={openLaunch} onCancel={cancelRun} onSelect={setSelRunId} onCompare={openDiff} prevOf={prevOf}
        onOpenReader={openReader} onAsk={askOwner}
        canShowAll={!allRuns && ((ov.runs || []).length >= 10)} onShowAll={showAll} />
    );
  } else if (tab === 'ledger') {
    body = window.CimLedgerTab
      ? <window.CimLedgerTab key={`ledger-${ledgerSub}`} engagementId={engagementId} phone={phone} onJobChange={setLedgerLive}
        initialSub={ledgerSub} />
      : <CimEmptyLine>The ledger isn't available.</CimEmptyLine>;
  } else if (tab === 'sections') {
    body = window.CimSectionsTab
      ? <window.CimSectionsTab engagementId={engagementId} phone={phone} onGoTab={goTab} chapterOf={sectionChapter}
        onOpenHandoff={(runId, n) => openReader(runId, 'handoff', n)} />
      : <CimEmptyLine>The sections aren't available.</CimEmptyLine>;
  } else if (tab === 'book') {
    body = window.CimBookTab
      ? <window.CimBookTab engagementId={engagementId} phone={phone} />
      : <CimEmptyLine>The book isn't available.</CimEmptyLine>;
  }

  return (
    <>
      {wrap(
        <>
          {header}
          {tabs}
          <div style={{ marginTop: 14 }}>{body}</div>
        </>,
      )}
      {launch && (
        <CimLaunchDrawer engagementId={engagementId} mode={launch.mode} baseRunId={launch.baseRunId} title={launch.title}
          mandate={ov.mandate} onClose={() => setLaunch(null)} onStarted={onStarted} />
      )}
      {reader && (!readerDetail || !readerDetail.run ? (
        <window.Drawer open onClose={() => setReader(null)} title={reader.kind === 'deliverable' ? `Deliverable ${reader.key}` : `Section ${_cimPad(reader.key)}`}>
          {readerDetail && readerDetail.error
            ? <div style={{ fontSize: 12.5, color: 'var(--rose-fg)' }}>{readerDetail.error}</div>
            : <window.SkeletonRows rows={6} />}
        </window.Drawer>
      ) : reader.kind === 'deliverable' ? (
        <CimDeliverableDrawer detail={readerDetail} number={reader.key} canReview={!!ov.can_run} busy={busy} phone={phone}
          onReview={(kind, key, status, note) => doReview(reader.runId, kind, key, status, note)} onClose={() => setReader(null)} />
      ) : (
        <CimHandoffDrawer detail={readerDetail} sectionNo={reader.key} folderLabel={folderLabel} canReview={!!ov.can_run} busy={busy}
          onReview={(kind, key, status, note) => doReview(reader.runId, kind, key, status, note)} onClose={() => setReader(null)} onRef={setRefOpen} />
      ))}
      {diff && <CimDiffDrawer state={diff} sectionTitle={sectionTitle} folderLabel={folderLabel} phone={phone} onClose={() => setDiff(null)} onRef={setRefOpen} />}
      {/* Over the drawer that cited it: the note modal, or the vault open on the file. */}
      {refOpen && refOpen.kind === 'note' && window.NoteModal && <window.NoteModal noteId={refOpen.id} onClose={() => setRefOpen(null)} />}
      {refOpen && refOpen.kind === 'file' && window.useVault && window.VaultManager && (
        <CimVaultScope companyId={cid}>
          {(v) => <window.VaultManager v={v} companyId={cid} initialFileId={refOpen.id} phone={phone} onClose={() => setRefOpen(null)} />}
        </CimVaultScope>
      )}
    </>
  );
};

window.CimCommandCenter = CimCommandCenter;
window.CimCommandCenterKit = Object.freeze({ tabHash: _cimTabHash, tabFromHash: _cimTabFromHash });
